/**
 * A category-axis tick that wraps its label onto a second line instead of
 * letting Recharts drop it.
 *
 * Recharts thins category ticks by measuring each label as a single line and
 * hiding any that would collide — so on a half-width dashboard tile the first
 * stage of "Pipeline by stage" silently disappeared. Rendering every tick
 * (`interval={0}`) and wrapping the long ones at a space keeps each category
 * named inside the band it owns.
 *
 * Bar charts only: a line/area chart's category axis is usually a date series
 * where thinning to every Nth label is the right answer, not wrapping.
 */

import type { ReactElement } from 'react';
import type { XAxisTickContentProps } from 'recharts/types/util/types';

/** Never more than two lines — a third would collide with the axis title. */
const MAX_LINES = 2;
/** Approximate glyph width as a fraction of the font size, for a UI sans face. */
const AVERAGE_GLYPH_EM = 0.58;
/** Narrowest budget we'll wrap to, so a cramped band still breaks at a space. */
const MIN_CHARACTER_BUDGET = 4;
/** Recharts' own `verticalAnchor='start'` baseline shift for a tick label. */
const FIRST_LINE_DY = '0.71em';
const NEXT_LINE_DY = '1.2em';
const ELLIPSIS = '…';

/**
 * Above this many categories a half-width tile's bands are too narrow for
 * even a wrapped word, so recharts' own thinning is the better answer.
 *
 * Twelve is where that turns over: a ~600px plot gives 50px per band, which
 * holds the ~8 characters a wrapped name needs ("Daniel Okafor" → "Daniel" /
 * "Okafor"). Below the cap every category keeps its name; above it, showing
 * some names beats overlapping all of them.
 */
export const MAX_UNTHINNED_CATEGORIES = 12;

/**
 * Height a second line of wrapped tick text adds to the axis band —
 * {@link NEXT_LINE_DY} at the 12px tick size, rounded up.
 */
export const WRAPPED_TICK_LINE_HEIGHT = 15;

/**
 * Greedy word wrap into at most {@link MAX_LINES} lines of roughly
 * `maxChars` characters each.
 *
 * A single word is never broken: "Qualification" reads better slightly wider
 * than its band than as "Qualificat…". Anything that would need a third line
 * is folded into the second with an ellipsis — the tooltip still carries the
 * full name.
 */
export function wrapCategoryLabel(label: string, maxChars: number): string[] {
  const words = label.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return [''];
  }
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (!current || candidate.length <= maxChars) {
      current = candidate;
      continue;
    }
    lines.push(current);
    current = word;
    if (lines.length === MAX_LINES) {
      break;
    }
  }
  if (lines.length < MAX_LINES) {
    lines.push(current);
    return lines;
  }
  // Ran out of lines with words still to place: mark the cut on the last one.
  const last = lines[MAX_LINES - 1];
  const trimmed =
    last.length >= maxChars ? last.slice(0, Math.max(1, maxChars - 1)) : last;
  lines[MAX_LINES - 1] = `${trimmed}${ELLIPSIS}`;
  return lines;
}

/**
 * Characters of `fontSize` text that fit in one category band, given an axis
 * `axisWidth` wide showing `tickCount` ticks. Returns `Infinity` when the
 * band isn't measurable yet (first paint), so the label renders unwrapped
 * rather than shredded.
 */
export function bandCharacterBudget(
  axisWidth: number,
  tickCount: number,
  fontSize: number
): number {
  if (axisWidth <= 0 || tickCount <= 0 || fontSize <= 0) {
    return Number.POSITIVE_INFINITY;
  }
  const bandWidth = axisWidth / tickCount;
  return Math.max(
    MIN_CHARACTER_BUDGET,
    Math.floor(bandWidth / (fontSize * AVERAGE_GLYPH_EM))
  );
}

/**
 * Recharts types an axis position as `number | string` because SVG accepts
 * both; every value it actually passes a tick is a number.
 */
function toPixels(value: number | string | undefined): number {
  if (typeof value === 'number') {
    return value;
  }
  const parsed = Number.parseFloat(value ?? '');
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Build the `tick` render callback for a wrapping category axis.
 *
 * Recharts calls a function-valued `tick` directly and uses whatever element
 * it returns, so this is a render callback rather than a component — which is
 * also why `fill`/`fontSize` are closed over here: Recharts only forwards
 * SVG props from an *object*-valued `tick`, never from a function one.
 */
export function categoryAxisTickRenderer({
  fill,
  fontSize,
}: {
  fill: string;
  fontSize: number;
}): (props: XAxisTickContentProps) => ReactElement {
  return function renderCategoryAxisTick({
    x: rawX,
    y: rawY,
    width: rawWidth,
    textAnchor,
    index,
    visibleTicksCount,
    className,
    payload,
    tickFormatter,
  }: XAxisTickContentProps): ReactElement {
    const x = toPixels(rawX);
    const y = toPixels(rawY);
    const raw = payload?.value;
    let label = '';
    if (tickFormatter) {
      label = tickFormatter(raw, index);
    } else if (raw !== undefined && raw !== null) {
      label = String(raw);
    }

    const budget = bandCharacterBudget(
      toPixels(rawWidth),
      visibleTicksCount,
      fontSize
    );
    // The baseline shift doubles as each line's identity: there is exactly one
    // first line and at most one continuation, so `dy` + text is a stable key.
    const spans = wrapCategoryLabel(label, budget).map((text, position) => ({
      text,
      dy: position === 0 ? FIRST_LINE_DY : NEXT_LINE_DY,
    }));

    return (
      <text
        x={x}
        y={y}
        fill={fill}
        fontSize={fontSize}
        textAnchor={textAnchor}
        className={className}
      >
        {spans.map(span => (
          <tspan key={`${span.dy}:${span.text}`} x={x} dy={span.dy}>
            {span.text}
          </tspan>
        ))}
      </text>
    );
  };
}
